/* eslint-disable complexity */
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { Avatar, AvatarGroup, Box, Button, Skeleton, Theme, Typography, useMediaQuery } from '@mui/material';
import Link from 'next/link';
import { compact, flatten, last, maxBy, random } from 'lodash-es';
import { useInView } from 'react-intersection-observer';
import Image from 'next/legacy/image';
import {
	DiscountTypeEnum,
	EventInstanceFilter,
	FileVariantsEnum,
	OrderDto,
	findUserToMembershipByCommunityId,
	formatEventTimeRange,
	OrderStatusEnum,
	EmptyAvatarVariantEnum,
	isEventPast,
	EventInstanceDto,
	priceFormatter,
	getLocalTimeZone,
	isPriceSoldOut,
	CreateEventTicketDto,
	canPreselectPriceTier,
	disableOpenPurchaseButton,
} from '@sweatpals/shared';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import CloseIcon from '@mui/icons-material/CloseRounded';
import SendIcon from '@mui/icons-material/Send';
import {
	Colors,
	EventBackground,
	Button as SharedButton,
	Showable,
	clsx,
	useStyles
} from '@sweatpals/react-ui-kit';
import { useQuery } from '@tanstack/react-query';
import { eventPageStyles } from './event-page.styles';
import Header from '../../ui-v3/header/header.component';
import Footer from '../../ui-v3/footer/footer.component';
import GetNewsLetterSection from '../../ui-v3/get-newsletter/get-newsletter-section.component';
import {
	eventsService,
	membershipsService,
	usersService,
	eventTicketsReservationService,
} from '../../../services';
import RemoteImage from '../../images/Remote-image';
import {
	APP_STORE_APP_PATH,
	CONTACT_US_SEND_EMAIL,
	GOOGLE_PLAY_APP_PATH,
	isClient,
	os,
	OS_ENUM,
	queryKeys,
	TYPEFORM_URL,
	ENABLE_MEMBERSHIP_UI,
	eventParticipantsPlaceholders,
} from '../../../constants';
import useLink from '../../../utils/hooks/useLink';
import LocationAddressLink from '../../links/LocationAddressLink';
import { getPublicFileUrl } from '../../../utils/file.utils';
import LinkifyText from '../../LinkifyText';
import {
	CalendarIcon,
	CheckIcon,
	LinkIcon,
	LocationIcon,
	LockIcon,
	MoneyIcon,
	MoreIcon,
	PersonIcon,
	ReportIcon,
	ShareIcon,
} from '../../icons';
import { FitnessTypeBadge } from '../../ui-v2/fitness-type-badge/fitness-type-badge.component';
import { CheckoutFormModal } from '../../ui-v3/checkout-form';
import MapBlock from '../../ui-v2/map-block/map-block.component';
import EventAuthors from '../../ui-v3/event-authors/event-authors.component';
import EventCommunity from '../../ui-v3/event-community/event-community.component';
import CommunityEvent from '../../ui-v3/community-events/community-event.component';
import { SentryFeedbackButton } from '../../SentryFeedbackButton';
import FullSizeLoader from '../../loaders/FullSizeLoader';
import ExternalLink from '../../links/External-link';
import useClipboard from '../../../utils/hooks/useClipboard';
import { OrderModal } from '../../ui-v3/order';
import { EventType } from '../../../services/types/event-types';
import { CoreTooltip } from '../../ui-v3/core-tooltip';
import { authStore } from '../../../stores';
import PriceTierCard from '../../ui-v2/price-tier-card/price-tier-card.component';
import { HostTabsEnum } from '../HostPage/host-page.component';
import { useFacebookPixel } from '../../../hooks/useFacebookPixel.hook';
import { useAttentive } from '../../../hooks/useAttentive.hook';
import { useGoogleAnalyticsTag } from '../../../hooks/useGoogleTag.hook';
import { JoinWaitlistModal, WaitlistUserData } from '../../ui-v3/join-waitlist';
import { FadeSpinner } from '../../ui-v3/fade-spinner';
import { SponsorList } from '../../ui-v3/sponsor-list';
import { CoreButtonBase } from '../../ui-v3/core-button';
import MarkdownText from '../../MarkdownText';
import CreateOwnEventSection from '../../ui-v3/create-own-event-block/create-own-event-block.component';
import { useTimeToEventStart } from '../../../hooks/use-time-to-event-start.hook';
import { useQueryEventReservedSpots } from '../../../hooks/events/use-query-event-reserved-spots.hook';
import { useEventAdditionalData } from '../../../hooks/events/use-event-additional-data.hook';
import { Editor as MarkdownEditorField } from '../../ui-v3/markdown-field/markdown-field';
import { useTickets } from '../../../hooks/events/use-tickets.hook';
import { useAnalytics } from '../../../hooks/use-analytics.hook';
import WarningModal from '../../ui-v3/warning-modal/warning-modal.component';
import EventPageLoader from './event-page.loader';
import OGMeta, { getEventSEOMeta, OGMetaProps } from '../../OGMeta';
import { useGetPublicEventInstanceByAliasQuery, usePrefetchGetPublicUserByUserNameQuery } from '../../../hooks/queries';

export enum EventTabsEnum {
	EVENT = 'event',
	TICKETS = 'tickets',
}

export interface EventPageProps {
	eventAlias: string;
	shortLocalInstance: string;
	// props for embeded rendering
	checkout?: string;
	waitlist?: string;
	disableCloseCheckout?: string;
	preselectedTierId?: string;
	preselectedTierQty?: string;
	ticketsJson?: string;
	tickets?: CreateEventTicketDto[];
	isBot?: boolean;
}

// eslint-disable-next-line complexity
const EventPage: FC<EventPageProps> = observer(({ eventAlias, shortLocalInstance, ...props }) => {
	const router = useRouter();
	const user = authStore.user;
	const { makeEventLink, makeHostLink } = useLink();
	const { copyTextToClipboard } = useClipboard();
	const { ref: joinButtonRef, inView: joinButtonInView } = useInView();

	const {
		checkout = props.checkout,
		waitlist = props.waitlist,
		preselectedTierId = props.preselectedTierId,
		preselectedTierQty = props.preselectedTierQty,
		ticketsJson = props.ticketsJson,
	} = router.query as { [key: string]: string };

	const preselectedTicketsParsed = useMemo(() => ticketsJson?.length ? JSON.parse(ticketsJson) as CreateEventTicketDto[] : props.tickets, [ticketsJson, props.tickets]);

	const [showWaitlistModal, setShowWaitlistModal] = useState(false);
	const [isEventFullDataLoaded, setIsEventFullDataLoaded] = useState(false);
	const [selectedBasePriceId, setSelectedBasePriceId] = useState<string | null>(null);
	const [isAlreadyJoinedWaitlist, setIsAlreadyJoinedWaitlist] = useState(false);
	const [showWaitlistJoinedTooltip, setShowWaitlistJoinedTooltip] = useState(false);
	const [isWaitlistJoinLoading, setIsWaitlistJoinLoading] = useState(false);
	const [isTierWarningModalOpen, setIsTierWarningModalOpen] = useState(false);
	const [eventData, setEventData] = useState<EventType | null>(null);

	const {
		refetch: refetchEvent,
		data: newEventData,
		isFetching: isEventFetching
	} = useGetPublicEventInstanceByAliasQuery(eventAlias, shortLocalInstance, 'event-page.component');

	const prefetchGetPublicUserByUserNameQuery = usePrefetchGetPublicUserByUserNameQuery();

	useEffect(() => {
		setEventData(null);
		setIsEventFullDataLoaded(false);
	}, [eventAlias]);

	useEffect(() => {
		if (!isEventFetching) {
			setEventData(newEventData ?? null);
		}
	}, [newEventData, isEventFetching]);

	// facebook pixel for specific event
	const { fbPixelNoScript, fbTrackEventTyped } = useFacebookPixel({ event: eventData });

	// attentive for specific event
	const { attnScript } = useAttentive({ event: eventData });

	// google tag for specific event
	const { gaScript } = useGoogleAnalyticsTag({ event: eventData });

	// use firebase analytics
	const { logEventTyped } = useAnalytics();

	// track event page view
	useEffect(() => {
		if (eventData) {
			logEventTyped('event_page_view', {
				baseEventId: eventData.baseId,
				authorId: eventData.authorId,
				eventInstance: eventData.instance instanceof Date ? eventData.instance.toISOString() : eventData.instance,
				alias: eventData.alias,
				eventType: eventData.isPaid ? 'paid' : 'free',
				pageType: 'event',
				eventId: eventData.id,
			});
		}
	}, [logEventTyped, eventData?.id]);

	const { isPurchaseAllowed: isPurchaseAllowedDueToSalesStartTime, timeLeftReadable } = useTimeToEventStart(eventData);
	const [isCheckoutFormModalOpen, setIsCheckoutFormModalOpen] = useState(false);
	const [isEventOrderModalOpen, setIsEventOrderModalOpen] = useState(false);
	const [shareMenuIsOpen, setShareMenuIsOpen] = useState(false);
	const [eventLinkIsCopied, setEventLinkIsCopied] = useState(false);
	const [order, setOrder] = useState<OrderDto | null>(null);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [editTiersDisabled, setEditTiersDisabled] = useState<boolean>(false);
	const [selectedShortLocalInstance, setSelectedShortLocalInstance] = useState<string>(null);
	const [eventInstances, setEventInstances] = useState<EventType[]>([]);
	const [eventInstancesFilter, setEventInstancesFilter] = useState<EventInstanceFilter>(null);

	const {
		eventId,
		instance,
		isRsvpListHidden,
		isPaidEvent,
		isPastEvent,
		participantsCount,
		host,
		coHost,
		canCheckoutAfterEndDate,
		priceString,
		eventShareLink,
		hostPageUrl,
		coHostPageUrl,
		leaders,
		sponsors,
	} = useEventAdditionalData({
		eventData,
		eventInstances,
		currentUser: user,
	});

	// Reset the state of event instances upon opening another event.
	useEffect(() => {
		if (eventId) {
			setEventInstances(isPastEvent ? [eventData] : []);
			setSelectedShortLocalInstance(shortLocalInstance);
			setEventInstancesFilter({ eventIds: [eventId], limit: 4, withUnverifiedEvents: true });
		}
	}, [eventId]);

	const {
		prices,
		publishedPrices,
		availablePublishedPrices,
		freeSpotsCount,
		isEventSoldOut,
		spotsLeftText,
		refetchEventTicketsCountAndSpots,
		refetchUserEventTicketReservations,
		eventTicketsCountAndSpots,
		userTicketReservations,
		isUserTicketReservationsFetching,
	} = useQueryEventReservedSpots(
		{
			eventId,
			instance,
			eventData,
		},
		10000
	);

	const {
		tickets,
		ticketsQuantity,
		setTickets,
		addTicket,
		removeTicket,
		clearTickets,
	} = useTickets({ prices, initialData: preselectedTicketsParsed });

	const isWaitlistEnabled = !!eventData?.isWaitlistEnabled;

	const eventParticipantsPlaceholder = useMemo(
		() => eventParticipantsPlaceholders[random(0, eventParticipantsPlaceholders.length - 1)],
		[]
	);
	const timezoneOffset = useMemo(() => {
		const hour = 60 * 60 * 1000;
		const timezoneOffset = getTimezoneOffset(eventData?.tzid ?? getLocalTimeZone());

		return `GMT${timezoneOffset < 0 ? '-' : '+'}${Math.abs(timezoneOffset) / hour}`;
	}, [eventData?.tzid]);

	const openEventOrderModal = useCallback((order: OrderDto): void => {
		setIsEventOrderModalOpen(true);
		setOrder(order);

		if (order.status === OrderStatusEnum.Successful) {
			if (order.event.isDonationBased) {
				// donation event
				fbTrackEventTyped('Donate', {
					value: (order.paymentDetails.amount - order.paymentDetails.fullFee) / 100,
					currency: 'USD',
					orderId: order.id,
					eventBaseId: order.eventId,
					instance: new Date(order.eventInstanceDate).toISOString(),
					alias: order.event?.alias,
				});
			} else if (order.event.isPaid) {
				// paid event
				fbTrackEventTyped('Purchase', {
					value: (order.paymentDetails.amount - order.paymentDetails.fullFee) / 100,
					currency: 'USD',
					orderId: order.id,
					eventBaseId: order.eventId,
					instance: new Date(order.eventInstanceDate).toISOString(),
					alias: order.event?.alias,
				});
			} else {
				// free event
				fbTrackEventTyped('FreeEventRegister', {
					orderId: order.id,
					eventBaseId: order.eventId,
					instance: new Date(order.eventInstanceDate).toISOString(),
					alias: order.event?.alias,
				});
			}
		}
	}, [fbTrackEventTyped]);
	const closeEventOrderModal = useCallback(() => {
		setIsEventOrderModalOpen(false);
		setOrder(null);
	}, []);

	const onCheckoutFormSubmit = useCallback(async (order: OrderDto): Promise<void> => {
		await refetchEvent();
		openEventOrderModal(order);
	}, [refetchEvent, openEventOrderModal]);

	const hostEventsFetch = useQuery(
		queryKeys.relatedEvents(host?.id),
		async () =>
			await eventsService.getPublicExtendEventsList({
				limit: 3,
				creatorsIds: [host?.id],
			}),
		{
			enabled: !!host?.id,
		}
	);

	const coHostEventsFetch = useQuery(
		queryKeys.relatedEvents(coHost?.id),
		async () =>
			await eventsService.getPublicExtendEventsList({
				limit: 3,
				creatorsIds: [coHost?.id],
			}),
		{
			enabled: !!coHost?.id,
		}
	);

	const isHostsEventsFetching = useMemo(
		() => (hostEventsFetch.isFetching || coHostEventsFetch.isFetching),
		[hostEventsFetch.isFetching, coHostEventsFetch.isFetching]
	);

	const hostEvents = useMemo(
		() => hostEventsFetch?.data?.data?.filter(event => event.baseId !== eventId)?.slice(0, 2) ?? [],
		[hostEventsFetch, eventId]
	);

	const coHostEvents = useMemo(
		() => coHostEventsFetch?.data?.data?.filter(event => event.baseId !== eventId)?.slice(0, 2) ?? [],
		[coHostEventsFetch, eventId]
	);

	const {
		data: newEventInstances,
		refetch: loadMoreEventInstances,
		isFetching: eventInstancesIsLoading,
	} = useQuery(
		queryKeys.relatedEvents(eventInstancesFilter?.eventIds?.[0]),
		async () => eventsService.getPublicExtendEventsList(eventInstancesFilter),
		{ enabled: !!eventInstancesFilter }
	);

	useEffect(() => {
		if (newEventInstances && !eventInstancesIsLoading) {
			setEventInstances(eventInstances => [
				...eventInstances,
				...(newEventInstances?.data?.filter(
					event => !eventInstances.find(e => e.id === event.id && e.instance === event.instance)
				) ?? []),
			]);
			setEventInstancesFilter(filter => ({
				...(filter ?? {}),
				periodFrom: last(newEventInstances?.data ?? [])?.startDate || null,
				lastEventId: newEventInstances?.lastEventId || null,
			}));
		}
	}, [newEventInstances, eventInstancesIsLoading]);

	const { data: hostWithFollowers, isFetching: isHostWithFollowersFetching } = useQuery(
		queryKeys.user(host?.id ?? ''),
		async () => usersService.getUserByIdPublic(host?.id, { withFriendsAndFollowers: true }),
		{ enabled: !!host?.id }
	);
	const { data: coHostWithFollowers, isFetching: isCoHostWithFollowersFetching } = useQuery(
		queryKeys.user(coHost?.id ?? ''),
		async () => usersService.getUserByIdPublic(coHost?.id, { withFriendsAndFollowers: true }),
		{ enabled: !!coHost?.id }
	);

	const openCheckoutFormModal = useCallback(() => {
		setIsCheckoutFormModalOpen(true);
	}, []);
	const closeCheckoutFormModal = useCallback(async () => {
		clearTickets();
		setEditTiersDisabled(false);
		setIsCheckoutFormModalOpen(false);

		if (eventId && instance && eventData?.ticketReservationTimeMinutes) {
			await eventTicketsReservationService.upsertUserEventTicketReservations(eventId, instance, []);
		}

		await refetchEventTicketsCountAndSpots();
		await refetchUserEventTicketReservations();
	}, [
		eventId,
		instance,
		clearTickets,
		eventData?.ticketReservationTimeMinutes,
		refetchEventTicketsCountAndSpots,
		refetchUserEventTicketReservations,
	]);

	const [isTierPreselected, setIsTierPreselected] = useState<boolean>(false);

	const isHostsFetching = useMemo(
		() => isHostWithFollowersFetching || isCoHostWithFollowersFetching,
		[isHostWithFollowersFetching, isCoHostWithFollowersFetching]
	);

	const hosts = useMemo(
		() => compact([hostWithFollowers, coHostWithFollowers]),
		[hostWithFollowers, coHostWithFollowers]
	);

	const openEventPage = useCallback(
		async (shortLocalInstance: string): Promise<void> => {
			setIsRefreshing(true);
			setSelectedShortLocalInstance(shortLocalInstance);

			try {
				const eventLink = makeEventLink({
					eventAlias: eventData?.alias,
					shortLocalInstance,
				});

				await router.push(eventLink);
			} finally {
				setIsRefreshing(false);
			}
		},
		[makeEventLink, eventData?.alias, router]
	);

	const shareEventLink = useCallback(async (): Promise<void> => {
		if (!eventData || !eventShareLink) {
			return;
		}

		// const eventShareLink = await getEventShareLink({ eventId: eventData.id, instanceDate: new Date(eventData.instance) });

		await navigator.share({ title: eventData?.name, url: eventShareLink });
	}, [eventData, eventShareLink]);

	const copyEventLinkToClipboard = useCallback(async (): Promise<void> => {
		if (!eventData || !eventShareLink) {
			return;
		}

		// const eventShareLink = await getEventShareLink({ eventId: eventData.id, instanceDate: new Date(eventData.instance) });

		await copyTextToClipboard(eventShareLink);
		setEventLinkIsCopied(true);
	}, [copyTextToClipboard, eventData, eventShareLink]);

	useEffect(() => {
		if (!eventLinkIsCopied) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setEventLinkIsCopied(false);
		}, 3000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [eventLinkIsCopied]);

	const openShareMenu = useCallback(() => {
		setShareMenuIsOpen(true);
	}, []);
	const closeShareMenu = useCallback(() => {
		setShareMenuIsOpen(false);
	}, []);

	const openJoinWaitlistModal = useCallback(
		(basePriceId: string = prices[0]?.basePriceId) => {
			if (!eventData) {
				return;
			}

			setSelectedBasePriceId(basePriceId);
			setShowWaitlistModal(true);
		},
		[eventData, prices]
	);

	const handleStartCheckout = useCallback(() => {
		// track event on register button click
		logEventTyped('register_button_click', {
			eventType: eventData?.isPaid ? 'paid' : 'free',
			pageType: 'event',
			baseEventId: eventData?.baseId,
			eventInstance: eventData?.instance instanceof Date ? eventData?.instance?.toISOString() : eventData?.instance,
		})

		if (!eventData || !canCheckoutAfterEndDate) {
			router.push(hostPageUrl);

			return;
		}

		if (isEventSoldOut) {
			openJoinWaitlistModal();

			return;
		}

		// reserve tickets on checkout modal mount
		openCheckoutFormModal();

		fbTrackEventTyped('InitiateCheckout', undefined);
	}, [
		eventData,
		canCheckoutAfterEndDate,
		isEventSoldOut,
		openCheckoutFormModal,
		fbTrackEventTyped,
		router,
		hostPageUrl,
		openJoinWaitlistModal
	]);

	useEffect(() => {
		if (!eventData || !canCheckoutAfterEndDate || isTierPreselected) {
			return;
		}

		setIsTierPreselected(true);

		if (checkout !== 'true') {
			if (
				canPreselectPriceTier({
					availablePublishedPrices,
					isPaidEvent,
					canCheckoutAfterEndDate,
					isEventSoldOut,
					isPurchaseAllowedDueToSalesStartTime,
					hasTicketReservationTimer: !!eventData.ticketReservationTimeMinutes,
				})
			) {
				addTicket(availablePublishedPrices[0].id);
			}

			return;
		}

		if (preselectedTierId) {
			const foundPrice = prices.find(x => x.id === preselectedTierId || x.basePriceId === preselectedTierId);

			if (!foundPrice || isPriceSoldOut(foundPrice, eventTicketsCountAndSpots, userTicketReservations)) {
				setIsTierWarningModalOpen(true);

				return;
			}

			setTickets([
				{
					eventPriceId: foundPrice.id,
					quantity: Math.max(
						(Number(preselectedTierQty) || 0),
						(foundPrice.minTicketsPerOrder ?? 1)
					),
				},
			]);
			setEditTiersDisabled(true);
			handleStartCheckout();

			return;
		}

		if (isPurchaseAllowedDueToSalesStartTime) {
			// select first ticket before opening the checkout form modal
			if (availablePublishedPrices.length && !preselectedTicketsParsed?.length) {
				addTicket(availablePublishedPrices[0].id);
			}

			handleStartCheckout();

			return;
		}
	}, [
		checkout,
		prices,
		eventData,
		isPaidEvent,
		isEventSoldOut,
		userTicketReservations,
		eventTicketsCountAndSpots,
		canCheckoutAfterEndDate,
		isTierPreselected,
		preselectedTierId,
		preselectedTierQty,
		availablePublishedPrices,
		isPurchaseAllowedDueToSalesStartTime,
		addTicket,
		setTickets,
		handleStartCheckout,
		preselectedTicketsParsed,
	]);

	const smMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

	const markers = useMemo(() => {
		if (!eventData || eventData.isOnlineEvent) {
			return [];
		}

		return [
			{
				addressName: eventData.addressName,
				position: {
					lat: Number(eventData.addressLat),
					lng: Number(eventData.addressLng),
				},
			},
		];
	}, [eventData]);

	const { data: hostMemberships = [] } = useQuery(
		queryKeys.hostMemberships(host?.id),
		async () => membershipsService.getPublishedMembershipsByCommunityId(host.id),
		{ enabled: !!host?.id }
	);
	const { data: userMemberships = [], refetch: refetchUserMemberships, isFetching: isUserMembershipsFetching } = useQuery(
		queryKeys.userMemberships(user?.id),
		async () => membershipsService.getCurrentUserMemberships(true),
		{
			enabled: !!user?.id,
		}
	);

	const maxMembershipDiscount = useMemo(() => {
		if (!prices.length) {
			return;
		}

		const publicHostMembershipIds = new Set((hostMemberships ?? []).filter(m => !m.isUnlisted).map(m => m.id));

		const discounts = flatten(
			prices.map(price => {
				const eventPriceToMemberships = price.eventPriceToMemberships
					?.filter(({ membershipId }) => publicHostMembershipIds.has(membershipId));

				if (!eventPriceToMemberships?.length) {
					return [];
				}

				return eventPriceToMemberships.map(discount => {
					const { amount, type } = discount;

					const discountInPercentage = type === DiscountTypeEnum.Fixed && amount
						? (price.priceAmount > amount ? (price.priceAmount / amount) * 100 : 100)
						: amount;

					return { discountInPercentage, amount, type };
				});
			})
		);

		return maxBy(discounts, 'discountInPercentage');
	}, [prices, hostMemberships]);

	const boughtMembership = useMemo(
		() => findUserToMembershipByCommunityId(userMemberships, eventData?.author?.id),
		[userMemberships, eventData?.author?.id]
	);

	const styles = useStyles(eventPageStyles, participantsCount);

	const joinButtonText = useMemo(() => {
		if (!canCheckoutAfterEndDate) {
			return 'More events from this host';
		}

		if (!eventData || isEventSoldOut) {
			return 'See other events';
		}

		if (!isPurchaseAllowedDueToSalesStartTime) {
			return `Sales start in ${timeLeftReadable}`;
		}

		if (eventData.isPaid && !eventData.isDonationBased) {
			return ticketsQuantity > 1
				? `Buy ${ticketsQuantity} tickets`
				: ticketsQuantity === 1
				? 'Buy ticket'
				: 'Select ticket tier to continue';
		}

		return 'Register';
	}, [canCheckoutAfterEndDate, eventData, isEventSoldOut, isPurchaseAllowedDueToSalesStartTime, timeLeftReadable, ticketsQuantity]);

	const isJoinButtonDisabled = useMemo(
		() =>
			disableOpenPurchaseButton({
				isPaidEvent,
				isPastEvent,
				isEventSoldOut,
				ticketsQuantity,
				isPurchaseAllowedDueToSalesStartTime,
			}),
		[isPaidEvent, isPastEvent, isEventSoldOut, ticketsQuantity, isPurchaseAllowedDueToSalesStartTime]
	);

	const joinWaitlistButtonText = useMemo<ReactNode>(() => {
		if (isAlreadyJoinedWaitlist) {
			return (
				<>
					<CheckIcon /> Joined waitlist
				</>
			);
		}

		return 'Join Waitlist';
	}, [isAlreadyJoinedWaitlist]);

	const handleJoinWaitlist = useCallback(
		async (userWaitlistData: WaitlistUserData, baseEventPriceId?: string): Promise<void> => {
			if (!eventData) {
				return;
			}

			try {
				setIsWaitlistJoinLoading(true);
				await eventsService.joinEventWaitlistV2(
					eventData.id,
					typeof eventData.instance === 'string' ? (eventData.instance as string) : eventData.instance.toISOString(),
					{
						...userWaitlistData,
						// if event is donation based, we don't need to pass price id
						baseEventPriceId: eventData.isDonationBased ? undefined : baseEventPriceId,
					}
				);

				setIsAlreadyJoinedWaitlist(true);
				setShowWaitlistModal(false);
				setShowWaitlistJoinedTooltip(true);
				setTimeout(() => {
					setShowWaitlistJoinedTooltip(false);
				}, 4000);
			} catch (error) {
				// do smth
				console.error(error);
			} finally {
				setIsWaitlistJoinLoading(false);
			}
		},
		[eventData]
	);

	useEffect(() => {
		if (waitlist === 'true' && eventData && !isAlreadyJoinedWaitlist) {
			openJoinWaitlistModal();
		}
	}, [eventData, isAlreadyJoinedWaitlist, openJoinWaitlistModal, waitlist]);

	useEffect(() => {
		if (
			!isEventFetching && !isUserTicketReservationsFetching && !isUserMembershipsFetching
			&& !!eventData && !isEventFullDataLoaded
		) {
			setIsEventFullDataLoaded(true);
		}
	}, [eventData, isEventFetching, isUserTicketReservationsFetching, isUserMembershipsFetching, isEventFullDataLoaded]);

	const seo = useMemo<OGMetaProps>(
		() => (props.isBot ? {} : getEventSEOMeta(eventData, eventInstances)),
		[eventData, eventInstances, props.isBot]
	);

	return (
	<>
		<OGMeta {...seo} />
		<Box sx={styles.root}>
			{fbPixelNoScript}

			{attnScript}

			{gaScript}

			<Showable hide={isCheckoutFormModalOpen}>
				<EventBackground
					sx={{ width: 'calc(100% - 4px)' }}
					background={
						eventData?.background
						&& getPublicFileUrl(eventData.background, {
							variant: FileVariantsEnum.XS,
						})
					}
				/>
			</Showable>

			<Box sx={styles.content}>
				<Header sx={{ position: 'fixed' }} />
				<Showable hide={isEventFullDataLoaded}>
					<EventPageLoader />
				</Showable>
				<Showable show={isEventFullDataLoaded}>
				<Showable show={eventLinkIsCopied}>
					<Box sx={styles.eventNotification}>
						<Typography>Link copied!</Typography>
					</Box>
				</Showable>
				<Box sx={styles.event}>
					<Box sx={styles.eventAvatarWrapper}>
						<Box
							sx={{
								...styles.eventAvatar,
								backgroundImage: eventData?.background
									? `url(${getPublicFileUrl(eventData.background, {
											variant: FileVariantsEnum.L,
										})})`
									: undefined,
							}}
						/>
						<Showable show={!smMedia}>
							<Box sx={styles.shortEventInfo}>
								<Showable show={host}>
									<EventAuthors host={host} coHost={coHost} onHostLinkClick={prefetchGetPublicUserByUserNameQuery}/>
								</Showable>
								<Box sx={styles.eventName}>{eventData?.name}</Box>
							</Box>
						</Showable>
					</Box>
					<Box flex={1}>
						<Box sx={styles.eventData}>
							<Box
								position='relative'
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								<Showable show={smMedia}>
									<Showable show={host}>
										<EventAuthors host={host} coHost={coHost} onHostLinkClick={prefetchGetPublicUserByUserNameQuery}/>
									</Showable>
									<Box sx={styles.eventName}>{eventData?.name}</Box>
								</Showable>
								<Showable show={eventInstances.length > 1 && !eventData?.additionalData?.hideEventDate}>
									<Box sx={styles.eventDates}>
										<Typography sx={styles.eventDataTitle}>Select date and time {timezoneOffset}</Typography>
										<Box sx={styles.eventDateList}>
											{eventInstances.map((eventInstance) => {
												const { startDate, instance, shortLocalInstance } = eventInstance;
												const date = new Date(instance ?? startDate);
												const isSelectedDate = selectedShortLocalInstance && selectedShortLocalInstance === shortLocalInstance;
												const isPastEvent = isEventPast(eventInstance as EventInstanceDto);
												const timezone = eventInstance?.tzid ?? getLocalTimeZone();

												return (
													<Box
														key={date.toISOString()}
														sx={clsx(
															styles.eventDate,
															{
																cursor: isRefreshing && isSelectedDate ? 'default' : 'pointer',

																'&:hover': {
																	background: isRefreshing && isSelectedDate ? '#0000000D' : '#FFFFFF0D',
																	boxShadow:
																		isRefreshing && isSelectedDate ? '0 0 0 1px #FFFFFF4D' : '0 0 0 1px #E8E8E8',
																},
															},
															isSelectedDate
																? {
																		backgroundColor: '#FFFFFF26 !important',
																		boxShadow: '0 0 0 2px #FFFFFFB2 !important',
																		cursor: 'default',
																	}
																: undefined,
															isPastEvent
																? {
																		p: '16px 10px 12px 10px',
																	}
																: undefined
														)}
														onClick={() => {
															openEventPage(shortLocalInstance);
														}}
													>
														<Showable show={isRefreshing && isSelectedDate}>
															<Box sx={styles.loader}>
																<FadeSpinner size={30} height={8} color='white' />
															</Box>
														</Showable>
														{isPastEvent ? (
															<Typography sx={styles.pastEventDateLabel}>Past Event</Typography>
														) : isSelectedDate ? (
															<CheckIcon />
														) : (
															<CalendarIcon />
														)}
														<Typography>{formatInTimeZone(date, timezone, 'eee LLL d')}</Typography>
														<Typography>{formatInTimeZone(date, timezone, 'p')}</Typography>
													</Box>
												);
											})}
											<Showable show={newEventInstances?.hasMore && !eventInstancesIsLoading}>
												<Box
													sx={styles.eventDate}
													onClick={() => {
														if (!eventInstancesIsLoading) {
															loadMoreEventInstances();
														}
													}}
												>
													<MoreIcon />
													<Typography>More</Typography>
													<Typography>Options</Typography>
												</Box>
											</Showable>
											<Showable show={eventInstancesIsLoading}>
												<Box sx={styles.eventDatesLoader}>
													<FullSizeLoader />
												</Box>
											</Showable>
										</Box>
									</Box>
								</Showable>
								<Box
									sx={{
										display: 'flex',
										flexDirection: { xs: 'column-reverse', sm: 'column' },
										gap: { xs: '20px', sm: '40px' },
									}}
								>
									<Box sx={styles.eventDataGrid}>
										<Showable show={!eventData?.additionalData?.hideEventDate}>
											<Box sx={styles.eventDataGridItem}>
												<CalendarIcon />
												<Box
													sx={{
														display: 'flex',
														flex: 1,
														gap: { xs: '4px', sm: '16px' },
														alignItems: { xs: 'flex-start', sm: 'center' },
														flexDirection: { xs: 'column', sm: 'row' },
													}}
												>
													<Typography sx={styles.eventDataGridItemText}>
														{eventData
															? formatEventTimeRange(eventData.startDate, eventData.endDate, {
																	timezone: eventData?.tzid,
																	startDateFormat: 'eee MMM do @ h:mm aa',
																	endDateFormat: 'eee MMM do @ h:mm aa',
																	timeFormat: 'h:mm aa',
																	separator: ' - ',
																})
															: ''}
													</Typography>
													<Showable show={isPastEvent}>
														<Typography sx={styles.pastEventLabel}>This is the past event</Typography>
													</Showable>
												</Box>
											</Box>
										</Showable>
										<Box sx={styles.eventDataGridItem}>
											<LocationIcon />
											<Typography
												sx={clsx(styles.eventDataGridItemText, {
													textDecoration: eventData?.isOnlineEvent ? 'none' : 'underline',
												})}
											>
												{eventData?.isOnlineEvent ? (
													'Online event'
												) : (
													<LocationAddressLink address={eventData?.addressName} />
												)}
											</Typography>
										</Box>
										<Box sx={styles.eventDataGridItem}>
											<Showable show={isEventFullDataLoaded}>
												<MoneyIcon />
												<Typography sx={styles.eventDataGridItemText}>
													{eventData?.isPaid
														? eventData?.isDonationBased
															? `Donation-based ${eventData?.minimalDonationAmount > 0 ? `(minimum ${priceFormatter(eventData.minimalDonationAmount)})` : ''}`
															: priceString
														: 'Free'}
												</Typography>
											</Showable>
											<Showable hide={isEventFullDataLoaded}>
												<Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
													<Skeleton variant='circular' width='24px' height='24px' sx={{ backgroundColor: Colors.white_a60 }} animation='wave' />
													<Skeleton variant='rectangular' width='112px' height='8px' sx={{ backgroundColor: Colors.white_a60, borderRadius: '12px' }} animation='wave' />
												</Box>
											</Showable>
										</Box>
										<Box sx={styles.eventDataGridItem}>
											<PersonIcon />
											<Showable hide={isRsvpListHidden}>
												<AvatarGroup
													max={4}
													total={participantsCount}
													variant='circular'
													sx={styles.eventRSVPAvatarGroup}
												>
													{eventData?.participants?.map(({ id, user }) => (
														<Avatar
															key={id}
															src={
																user.avatarId &&
																getPublicFileUrl(user.avatarId, {
																	variant: FileVariantsEnum.XS,
																})
															}
															alt={user.fullName}
															title={user.fullName}
														>
															<RemoteImage alt={user.fullName} src={null} width={56} height={56} />
														</Avatar>
													))}
												</AvatarGroup>
												<Showable show={spotsLeftText}>
													<Typography sx={styles.eventDataGridItemText}>{spotsLeftText}</Typography>
												</Showable>
											</Showable>
											<Showable show={isRsvpListHidden}>
												<CoreTooltip
													arrow
													title='The guest list for this event is private and is only visible to the host.'
													placement='right'
												>
													<Box sx={{ height: '32px', display: 'flex', alignItems: 'center', ml: '-10px' }}>
														<Image src={eventParticipantsPlaceholder} alt='event-participants-placeholder' />
													</Box>
												</CoreTooltip>
											</Showable>
										</Box>
									</Box>
									<Box
										sx={{
											width: '100%',
											maxWidth: '645px',
											display: 'flex',
											flexDirection: 'column',
											gap: { xs: '20px', sm: '24px' },
										}}
									>
										<Showable show={canCheckoutAfterEndDate}>
											<Showable show={isEventSoldOut && isWaitlistEnabled}>
												<Box sx={styles.eventSoldOutPlaceholder}>
													<Box sx={styles.eventSoldOutPlaceholderTextBox}>
														<Typography sx={styles.eventSoldOutPlaceholderTitle}>Sold out!</Typography>
														<Typography sx={styles.eventSoldOutPlaceholderDescription}>
															Join waitlist or try other dates or view other available events
														</Typography>
													</Box>

													<SharedButton
														transparent
														sx={{
															width: '220px',
															maxHeight: '42px',
															fontSize: '12px',
														}}
														onClick={async () => openJoinWaitlistModal()}
													>
														{joinWaitlistButtonText}
													</SharedButton>
												</Box>
											</Showable>
											<Showable show={isPaidEvent && !isEventSoldOut}>
												{publishedPrices.length ? (
													<Box display='flex' flexDirection='column' gap='8px'>
														<Typography sx={styles.eventDataTitle}>Select ticket tier:</Typography>
														{publishedPrices.map((tier, index) => (
															<PriceTierCard
																key={tier.id}
																index={index}
																priceTier={tier}
																isSmallSize={!smMedia}
																freeSpotsCount={freeSpotsCount && freeSpotsCount - ticketsQuantity}
																onlySingleAttendanceAllowed={eventData?.onlySingleAttendanceAllowed}
																isPurchaseAllowedDueToSalesStartTime={isPurchaseAllowedDueToSalesStartTime}
																eventTicketsCountAndSpots={eventTicketsCountAndSpots}
																userTicketReservations={userTicketReservations}
																sx={{ minWidth: '100%' }}
																joinWaitlistButtonText={joinWaitlistButtonText}
																quantity={tickets?.find(t => t.eventPriceId === tier.id)?.quantity}
																// TODO change to `price.isWaitlistEnabled` when implemented
																hideWaitlist={!isWaitlistEnabled}
																onJoinWaitlist={async () => openJoinWaitlistModal(tier.basePriceId)}
																onAdd={() => {
																	addTicket(tier.id);
																}}
																onRemove={() => {
																	removeTicket(tier.id);
																}}
																onSubscribe={() => {
																	clearTickets();
																	addTicket(tier.id);
																}}
															/>
														))}
													</Box>
												) : (
													<Box sx={styles.tiersPlaceholder}>
														<LockIcon style={{ width: '24px', height: '24px', color: Colors.white }} />
														<Typography sx={styles.tiersPlaceholderTitle}>
															No public tiers are available
														</Typography>
														<Typography sx={styles.tiersPlaceholderDescription}>
															If you're interested in the event, please reach out to the host for access.
														</Typography>
														<Button
															disableRipple
															sx={styles.contactHostButton}
															href={TYPEFORM_URL}
															target='_blank'
															rel='noreferrer noopener'
														>
															<SendIcon sx={{ fontSize: '16px' }} />
															Contact Host
														</Button>
													</Box>
												)}
											</Showable>
										</Showable>
										<Showable hide={isPaidEvent && !publishedPrices.length}>
											<CoreButtonBase
												ref={joinButtonRef}
												sx={clsx(styles.joinButton, { alignSelf: 'center', minWidth: '320px', width: '100%' })}
												disabled={isJoinButtonDisabled}
												onClick={handleStartCheckout}
											>
												{joinButtonText}
											</CoreButtonBase>
										</Showable>
									</Box>
								</Box>
								<Showable show={maxMembershipDiscount?.amount && ENABLE_MEMBERSHIP_UI}>
									<Box sx={styles.membershipBox}>
										<Box sx={styles.membershipTextBox}>
											<Typography sx={styles.membershipTitle}>
												RSVP for{' '}
												{maxMembershipDiscount?.discountInPercentage < 100
													? `${maxMembershipDiscount?.amount}${
															maxMembershipDiscount?.type === DiscountTypeEnum.Fixed ? '$' : '%'
														} discount`
													: 'FREE'}{' '}
												as a member
											</Typography>
											<Typography sx={styles.membershipDescription}>
												{host?.fullName} provides unique opportunity to join their community on special terms.
											</Typography>
										</Box>

										<ExternalLink href={`${hostPageUrl}/${HostTabsEnum.MEMBERSHIP}`}>
											<Button sx={styles.membershipButton}>Learn more</Button>
										</ExternalLink>
									</Box>
								</Showable>

								<Showable show={eventData?.description}>
									<Box sx={styles.eventDescription}>
										<Typography sx={styles.eventDescriptionTitle}>ABOUT</Typography>
										<Box sx={styles.eventDescriptionText}>
											{eventData?.additionalData?.isDescriptionInMarkdown ? (
												<MarkdownText color='inherit' linkColor='inherit'>
													{eventData?.description}
												</MarkdownText>
											) : (
												<LinkifyText>
													<MarkdownEditorField markdown={eventData?.description} />
												</LinkifyText>
											)}
										</Box>
									</Box>
								</Showable>
								<Showable show={eventData?.fitnessTypes.length}>
									<Box sx={styles.eventFitnessTypes}>
										{eventData?.fitnessTypes?.map(fitnessType => (
											<FitnessTypeBadge
												key={fitnessType.id}
												fitnessType={fitnessType.interest}
												masteryLevel={fitnessType.masteryLevel}
											/>
										))}
									</Box>
								</Showable>

								<Showable show={isHostsFetching}>
									<Box sx={styles.communities}>
										<Skeleton variant="text" sx={{ height: '24px', backgroundColor: Colors.white_a30 }}/>
										<EventCommunity showSkeleton />
									</Box>
								</Showable>
								<Showable show={hosts.length && !isHostsFetching}>
									<Box sx={styles.communities}>
										<Typography sx={styles.communitiesTitle}>
											Meet the host{hosts.length > 1 ? 's' : ''}
										</Typography>
										{hosts.map(host => (
											<EventCommunity key={host.id} community={host} onHostLinkClick={prefetchGetPublicUserByUserNameQuery} />
										))}
									</Box>
								</Showable>

								<Showable show={leaders.length}>
									<SponsorList
										title='Leaders'
										users={leaders.map(leader => ({
											...leader,
											url: makeHostLink({ userName: leader.userName }),
										}))}
									/>
								</Showable>
								<Showable show={sponsors.length}>
									<SponsorList
										title='Sponsors'
										users={sponsors.map(sponsor => ({
											id: sponsor.id,
											fullName: sponsor.sponsorName,
											avatarId: sponsor.avatarId,
											emptyAvatarVariant: EmptyAvatarVariantEnum.Orange,
											url: sponsor.sponsorUrl,
										}))}
									/>
								</Showable>

								<Showable show={!eventData?.isOnlineEvent}>
									<Box sx={styles.eventMapBox}>
										<Typography sx={styles.eventMapTitle}>Where to find us</Typography>
										<Showable show={isClient}>
											<MapBlock markers={markers} zoom={18} variant='dark' />
										</Showable>
									</Box>
								</Showable>

								<Showable show={isHostsEventsFetching}>
									<Box display='flex' flexDirection='column' gap='40px'>
										<Box sx={styles.separator} />
										<Box display='flex' flexDirection='column' gap='26px'>
											<Skeleton
												variant="text"
												sx={clsx(styles.communityEventsTitle, { backgroundColor: Colors.white_a30 })}
											/>
											<CommunityEvent showSkeleton />
										</Box>
									</Box>
								</Showable>

								<Showable show={(hostEvents?.length || coHostEvents?.length) && !isHostsEventsFetching}>
									<Box display='flex' flexDirection='column' gap='40px'>
										<Box sx={styles.separator} />
										<Showable show={hostEvents?.length}>
											<Box display='flex' flexDirection='column' gap='26px'>
												<Box display='flex' justifyContent='space-between' alignItems='center'>
													<Typography sx={styles.communityEventsTitle}>
														More events from <span>{host?.fullName}</span>
													</Typography>
													<Link href={hostPageUrl} passHref legacyBehavior>
														<Box sx={styles.link}>See All</Box>
													</Link>
												</Box>
												{hostEvents?.map(event => (
													<CommunityEvent
														key={`${event.id}-${new Date(event.instance ?? event.startDate).toISOString()}`}
														event={event}
													/>
												))}
											</Box>
										</Showable>
										<Showable show={coHostEvents?.length}>
											<Box display='flex' flexDirection='column' gap='26px'>
												<Box display='flex' justifyContent='space-between' alignItems='center'>
													<Typography sx={styles.communityEventsTitle}>
														More events from <span>{coHost?.fullName}</span>
													</Typography>
													<Link href={coHostPageUrl} passHref legacyBehavior>
														<Box sx={styles.link}>See All</Box>
													</Link>
												</Box>
												{coHostEvents?.map(event => (
													<CommunityEvent
														key={`${event.id}-${new Date(event.instance ?? event.startDate).toISOString()}`}
														event={event}
													/>
												))}
											</Box>
										</Showable>
										<CreateOwnEventSection />
									</Box>
								</Showable>
							</Box>

							<Showable show={showWaitlistJoinedTooltip}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										padding: '8px 16px',
										borderRadius: '100px',
										background: Colors.gray_a70,
										backdropFilter: 'blur(4.5px)',
										position: 'fixed',
										right: { xs: '27px', lg: 'calc(600px + 27px)', xl: 'calc(700px + 27px)' },
										bottom: { xs: '30px' },
										zIndex: theme => theme.zIndex.tooltip,
									}}
								>
									<Box
										sx={{
											fontSize: '28px',
										}}
									>
										💙
									</Box>
									<Typography
										sx={{
											fontFamily: 'Hind',
											fontSize: '18px',
											fontWeight: 800,
											color: Colors.white,
										}}
									>
										Waitlist joined
									</Typography>
								</Box>
							</Showable>
						</Box>
					</Box>
				</Box>
				<Showable show={!joinButtonInView}>
					<Box component='footer' sx={styles.eventFooter}>
						<Showable show={!smMedia && shareMenuIsOpen}>
							<Box sx={styles.eventActionsMenu}>
								<Box sx={styles.eventActionsMenuHeader}>
									<Typography sx={styles.eventActionsMenuHeaderText}>View this event on app!</Typography>
									<ExternalLink href={os === OS_ENUM.iOS ? APP_STORE_APP_PATH : GOOGLE_PLAY_APP_PATH}>
										<Typography sx={styles.eventActionsMenuHeaderLink}>Download app</Typography>
									</ExternalLink>
								</Box>
								<Box sx={styles.eventActionsMenuContent}>
									<Showable show={shareMenuIsOpen}>
										<Button
											sx={{ ...styles.button, borderColor: '#FFFFFF', width: '100%' }}
											onClick={shareEventLink}
											// disabled={!!eventShareLinkError}
										>
											Share
										</Button>
										<Button
											sx={{ ...styles.button, ...styles.secondaryButton, width: '100%' }}
											onClick={copyEventLinkToClipboard}
											// disabled={!!eventShareLinkError}
										>
											<LinkIcon />
											Copy Link
										</Button>
										<SentryFeedbackButton
											sx={{
												...styles.button,
												...styles.secondaryButton,
												...styles.sentryFeedbackButton,
												width: '100%',
											}}
										>
											<ReportIcon />
											Report a Bug
										</SentryFeedbackButton>
									</Showable>
								</Box>
							</Box>
						</Showable>
						<Box sx={{ display: 'flex', gap: { xs: '8px', md: '16px' }, width: '100%' }}>
							<CoreButtonBase
								sx={styles.joinButton}
								disabled={isJoinButtonDisabled}
								onClick={handleStartCheckout}
							>
								{joinButtonText}
							</CoreButtonBase>
							<Showable show={!smMedia}>
								<Button
									sx={{ ...styles.button, ...styles.secondaryButton, ...styles.iconButton }}
									onClick={shareMenuIsOpen ? closeShareMenu : openShareMenu}
								>
									{shareMenuIsOpen ? <CloseIcon /> : <MoreIcon />}
								</Button>
							</Showable>
							<Showable show={smMedia}>
								<Box sx={styles.footerSeparator} />
								<Button
									sx={{ ...styles.button, width: '130px' }}
									onClick={shareEventLink}
									// disabled={!!eventShareLinkError}
								>
									{/* {`isEventShareLinkLoading ? (
									<CircularProgress style={{ width: '24px', height: '24px' }} />
								) : (
									<ShareIcon />
								)`} */}
									<ShareIcon />
									Share
								</Button>
								<SentryFeedbackButton sx={{ ...styles.button, ...styles.sentryFeedbackButton, width: '72px' }}>
									<ReportIcon />
								</SentryFeedbackButton>
							</Showable>
						</Box>
					</Box>
				</Showable>
				<GetNewsLetterSection />
				<Footer />
				</Showable>
			</Box>

			<Showable show={isCheckoutFormModalOpen}>
				{() => (
					<CheckoutFormModal
						showSkeleton={!isEventFullDataLoaded}
						eventId={eventId}
						instance={instance}
						eventData={eventData}
						prices={prices}
						publishedPrices={publishedPrices}
						preselectedTickets={tickets}
						hostData={host}
						hostEvents={hostEvents}
						editTiersDisabled={editTiersDisabled}
						hostMemberships={hostMemberships}
						boughtMembership={boughtMembership}
						isOpen={isCheckoutFormModalOpen}
						freeSpotsCount={freeSpotsCount}
						eventTicketsCountAndSpots={eventTicketsCountAndSpots}
						userTicketReservations={userTicketReservations}
						onClose={closeCheckoutFormModal}
						onSubmit={onCheckoutFormSubmit}
						refetchEvent={refetchEvent}
						refetchUserMemberships={refetchUserMemberships}
						refetchEventTicketsCountAndSpots={refetchEventTicketsCountAndSpots}
						refetchUserEventTicketReservations={refetchUserEventTicketReservations}
					/>
				)}
			</Showable>

			<OrderModal
				open={isEventOrderModalOpen}
				host={host}
				coHost={coHost}
				orderId={order?.id}
				hostEvents={hostEvents}
				coHostEvents={coHostEvents}
				eventData={eventData}
				onClose={closeEventOrderModal}
				userMembership={ENABLE_MEMBERSHIP_UI ? boughtMembership : undefined}
			/>

			<JoinWaitlistModal
				showSkeleton={isWaitlistJoinLoading}
				open={showWaitlistModal}
				onClose={() => {
					setShowWaitlistModal(false);
				}}
				isAlreadyJoined={isAlreadyJoinedWaitlist}
				onSuccess={async data => handleJoinWaitlist(data, selectedBasePriceId)}
				loading={isWaitlistJoinLoading}
				disabled={isWaitlistJoinLoading}
			/>

			<WarningModal
				open={isTierWarningModalOpen}
				title='Sorry, this tier is currently unavailable'
				text={
					<React.Fragment>
						Please contact support for more information
						{' '}
						<ExternalLink href={`mailto:${CONTACT_US_SEND_EMAIL}`}>sweatpals@help.com</ExternalLink>
						{' '}
						or contact host
					</React.Fragment>
				}
				titleSx={{
					fontSize: '18px',
					fontWeight: 600,
				}}
				rootSx={{ width: '540px', }}
				submitButtonText="Okay"
				onClose={() => {
					setIsTierWarningModalOpen(false);
				}}
			/>
		</Box>
	</>
	);
});

export default EventPage;
